// PostHog Analytics Helper
// Centralized analytics functions for type-safe event tracking

declare global {
  interface Window {
    posthog?: {
      capture: (event: string, properties?: Record<string, unknown>) => void;
      identify: (distinctId: string, properties?: Record<string, unknown>) => void;
      reset: () => void;
    };
  }
}

// Core tracking function
export const trackEvent = (event: string, properties?: Record<string, unknown>) => {
  if (typeof window !== 'undefined' && window.posthog) {
    window.posthog.capture(event, properties);
  }
};

// Identify user (for order completion)
export const identifyUser = (email: string, properties?: Record<string, unknown>) => {
  if (typeof window !== 'undefined' && window.posthog) {
    window.posthog.identify(email, properties);
  }
};

// Pre-defined analytics events for conversion funnel
export const analytics = {
  // Page views
  pageView: (page: string, properties?: Record<string, unknown>) => 
    trackEvent('page_view', { page, ...properties }),
  
  // Section visibility tracking
  sectionViewed: (section: string, scrollDepth?: number) => 
    trackEvent('section_viewed', { section, scroll_depth: scrollDepth }),
  
  // Pack/Pricing interactions
  packSelected: (pack: string, videos: number, price: number, currency: string) => 
    trackEvent('pack_selected', { pack, videos, price, currency }),
  
  sliderChanged: (videos: number, price: number) => 
    trackEvent('slider_changed', { videos, price }),
  
  // Video interactions
  videoPlayed: (videoId: string, business: string, industry: string) => 
    trackEvent('video_played', { video_id: videoId, business, industry }),
  
  videoLiked: (videoId: string, business: string) => 
    trackEvent('video_liked', { video_id: videoId, business }),
  
  // CTA clicks
  ctaClicked: (cta: string, location: string) => 
    trackEvent('cta_clicked', { cta, location }),
  
  // Order form funnel
  orderPageViewed: (pack?: string) => 
    trackEvent('order_page_viewed', { pack }),
  
  formStepCompleted: (step: number, stepName: string) => 
    trackEvent('form_step_completed', { step, step_name: stepName }),
  
  formFieldFilled: (field: string, stepNumber: number) => 
    trackEvent('form_field_filled', { field, step: stepNumber }),
  
  checkoutStarted: (pack: string, total: number, currency: string, videoCount: number) => 
    trackEvent('checkout_started', { pack, total, currency, video_count: videoCount }),
  
  // Order completion
  orderCompleted: (orderId: string, total: number, currency: string, pack: string, email: string) => {
    identifyUser(email, { pack, total_spent: total });
    trackEvent('order_completed', { 
      order_id: orderId, 
      total, 
      currency,
      pack,
      email 
    });
  },
  
  // FAQ interactions
  faqExpanded: (question: string, category: string) => 
    trackEvent('faq_expanded', { question, category }),
  
  // ROI Calculator
  roiCalculated: (impressions: number, conversionRate: number, orderValue: number, calculatedRoi: number) => 
    trackEvent('roi_calculated', { 
      impressions, 
      conversion_rate: conversionRate, 
      order_value: orderValue, 
      calculated_roi: calculatedRoi 
    }),
  
  // Add-on interactions
  addOnToggled: (addOn: string, selected: boolean, price: number) => 
    trackEvent('add_on_toggled', { add_on: addOn, selected, price }),
  
  // Voice recording
  voiceRecordingStarted: () => 
    trackEvent('voice_recording_started'),
  
  voiceRecordingCompleted: (durationSeconds: number) => 
    trackEvent('voice_recording_completed', { duration_seconds: durationSeconds }),
  
  // File uploads
  fileUploaded: (type: 'logo' | 'store_photo' | 'product_image', count: number) => 
    trackEvent('file_uploaded', { type, count }),
};

export default analytics;
